import React, { useState, useEffect, useCallback } from 'react';
import { tokens as tk } from '../theme';
import EmployeeChart from './EmployeeChart';

const API = 'https://tcio-api.onrender.com';
const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('tcio_token') });
const DOT = '\u00B7', DASH = '\u2014', ARR = '\u2192', X = '\u2715';
const card = { background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusCard, boxShadow: '0 2px 8px rgba(34,48,62,0.06)' };
const lab = { fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: tk.textSecondary, fontWeight: 600 };
const fmtD = (d) => d ? new Date(String(d).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : DASH;

function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => { const h = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return m;
}

export default function EmployeeRecords({ user, openUser, employerOrg }) {
  const isMobile = useIsMobile();
  const [roster, setRoster] = useState([]);
  const [exp, setExp] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('active');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const [expModal, setExpModal] = useState(null);

  const orgQ = employerOrg ? '?employer_org_id=' + employerOrg : '';
  const load = useCallback(async () => {
    setErr('');
    try {
      const [r1, r2] = await Promise.all([
        fetch(API + '/api/hr/roster' + orgQ, { headers: auth() }).then(r => r.json()),
        fetch(API + '/api/hr/expirations' + (orgQ ? orgQ + '&' : '?') + 'days=90', { headers: auth() }).then(r => r.json()),
      ]);
      if (r1.ok) setRoster(r1.roster); else setErr(r1.error || 'load failed');
      if (r2.ok) setExp(r2.items);
    } catch (e) { setErr('network'); }
    setLoading(false);
  }, [orgQ]);
  useEffect(() => { setLoading(true); setSel(null); load(); }, [load]);
  useEffect(() => { if (openUser) setSel(openUser); }, [openUser]);

  const counts = { active: 0, leave: 0, terminated: 0 };
  roster.forEach(r => { counts[r.employment_status] = (counts[r.employment_status] || 0) + 1; });
  const rows = roster
    .filter(r => filter === 'all' || r.employment_status === filter)
    .filter(r => !q || (r.last_name + ' ' + r.first_name + ' ' + (r.position_title || '') + ' ' + (r.department || '')).toLowerCase().includes(q.toLowerCase()));
  const expIn = (lo, hi) => exp.filter(x => x.days_left >= lo && x.days_left <= hi);
  const btn = (primary) => ({ font: 'inherit', fontSize: 12.5, fontWeight: 600, padding: '7px 14px', borderRadius: tk.radiusControl, cursor: 'pointer',
    border: '1px solid ' + (primary ? tk.ai : tk.border), background: primary ? tk.ai : tk.surface, color: primary ? tk.surface : tk.ink });

  if (sel) return <EmployeeChart userId={sel} onClose={() => setSel(null)} onChanged={load} />;

  const strip = (label, color, lo, hi) => {
    const items = expIn(lo, hi);
    return (
      <div style={{ ...card, borderTop: '3px solid ' + color, padding: 14, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={lab}>{label}</span>
          <span style={{ fontSize: 22, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{items.length}</span>
        </div>
        {items.slice(0, 4).map(x => (
          <div key={x.kind + x.id} onClick={() => setSel(x.user_id)} style={{ fontSize: 12, padding: '5px 0', borderBottom: '1px solid ' + tk.borderSoft, cursor: 'pointer' }}>
            <span style={{ fontWeight: 600, color: tk.ink }}>{x.last_name}</span>
            <span style={{ color: tk.textSecondary }}> {DOT} {x.title.replace(/\s*\(.*\)$/, '')} </span>
            <span style={{ color: x.days_left < 0 ? tk.danger : color, fontWeight: 600 }}>{x.days_left < 0 ? 'OVERDUE' : x.days_left + 'd'}</span>
          </div>))}
        {items.length > 4 && <button onClick={() => setExpModal({ label, items })}
          style={{ font: 'inherit', fontSize: 12, fontWeight: 600, color: tk.ai, background: 'none', border: 'none', padding: '6px 0 0', cursor: 'pointer' }}>View all {items.length} {ARR}</button>}
        {!items.length && <div style={{ fontSize: 12, color: tk.textSecondary, paddingTop: 4 }}>Nothing in this window.</div>}
      </div>);
  };

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: tk.ink, letterSpacing: '-0.3px' }}>Employee Records</div>
        <div style={{ fontSize: 12.5, color: tk.textSecondary }}>Personnel files, compliance clocks, and credentials {DOT} roster of record</div>
      </div>
      {err && <div style={{ ...card, padding: 14, fontSize: 13, color: tk.danger, marginBottom: 12 }}>{err} <button style={{ ...btn(false), marginLeft: 8 }} onClick={() => { setLoading(true); load(); }}>Retry</button></div>}
      {loading ? <div style={{ ...card, height: 160, background: tk.surfaceSoft }} /> : (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
            {strip('Next 30 days', tk.danger, -3650, 30)}
            {strip('31\u201360 days', tk.warning, 31, 60)}
            {strip('61\u201390 days', tk.ai, 61, 90)}
          </div>
          <div style={{ ...card, padding: 16 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
              {[['active', 'Active (' + counts.active + ')'], ['leave', 'Leave (' + counts.leave + ')'], ['terminated', 'Terminated (' + counts.terminated + ')'], ['all', 'All']].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)} style={{ ...btn(filter === k), borderRadius: 999 }}>{l}</button>))}
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, position, department"
                style={{ font: 'inherit', fontSize: 13, padding: '8px 12px', borderRadius: tk.radiusControl, border: '1px solid ' + tk.border, color: tk.ink, background: tk.surface, flex: 1, minWidth: 180 }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr auto' : '220px 1fr 170px 90px', gap: 10, padding: '6px 0', borderBottom: '2px solid ' + tk.border }}>
              <span style={lab}>Name</span>
              {!isMobile && <span style={lab}>Position</span>}
              {!isMobile && <span style={lab}>File completeness</span>}
              <span style={{ ...lab, textAlign: 'right' }}>{isMobile ? 'File' : 'Attention'}</span>
            </div>
            {!rows.length && <div style={{ fontSize: 13, color: tk.textSecondary, padding: '14px 0' }}>No one matches.</div>}
            {rows.map(r => {
              const pct = r.req_total ? Math.round(r.req_done / r.req_total * 100) : 0;
              return (
                <div key={r.user_id} onClick={() => setSel(r.user_id)}
                  style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr auto' : '220px 1fr 170px 90px', gap: 10, padding: '10px 0',
                    borderBottom: '1px solid ' + tk.borderSoft, cursor: 'pointer', alignItems: 'center' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: tk.ink }}>
                    {r.last_name}, {r.first_name}
                    {r.is_remote && <span style={{ fontSize: 10, fontWeight: 700, color: tk.ai, border: '1px solid ' + tk.ai, borderRadius: 999, padding: '1px 6px', marginLeft: 6 }}>R</span>}
                  </span>
                  {!isMobile && <span style={{ fontSize: 13, color: tk.textSecondary }}>{r.position_title || DASH}{r.employment_status === 'terminated' && r.termination_period ? ' ' + DOT + ' ' + r.termination_period : ''}</span>}
                  {!isMobile && <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ flex: 1, height: 7, borderRadius: 4, background: tk.surfaceSoft, overflow: 'hidden' }}>
                      <span style={{ display: 'block', width: pct + '%', height: '100%', background: pct === 100 ? tk.success : pct >= 75 ? tk.ai : tk.warning }} /></span>
                    <span style={{ fontSize: 12, color: tk.textSecondary, fontVariantNumeric: 'tabular-nums', minWidth: 42 }}>{r.req_done}/{r.req_total}</span>
                  </span>}
                  <span style={{ textAlign: 'right', fontSize: 12.5, fontWeight: 600, color: r.req_attention ? tk.warning : tk.success }}>
                    {isMobile ? pct + '%' : r.req_attention ? r.req_attention + ' open' : 'Clear'}</span>
                </div>);
            })}
          </div>
        </div>)}

      {expModal &&
        <div onClick={() => setExpModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,34,57,0.45)', zIndex: 75, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ ...card, width: isMobile ? '100%' : 520, maxHeight: '75vh', overflowY: 'auto', borderRadius: isMobile ? '14px 14px 0 0' : tk.radiusCard, padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 14.5, fontWeight: 600, color: tk.ink }}>{expModal.label} {DOT} {expModal.items.length} items</span>
              <button style={btn(false)} onClick={() => setExpModal(null)}>{X}</button>
            </div>
            {expModal.items.map(x => (
              <div key={x.kind + x.id} onClick={() => { setExpModal(null); setSel(x.user_id); }}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderBottom: '1px solid ' + tk.borderSoft, cursor: 'pointer', fontSize: 12.5 }}>
                <span style={{ color: tk.ink }}><b>{x.last_name}, {x.first_name}</b> <span style={{ color: tk.textSecondary }}>{DOT} {x.title}</span></span>
                <span style={{ color: x.days_left < 0 ? tk.danger : tk.textSecondary, whiteSpace: 'nowrap', fontWeight: 600 }}>{x.days_left < 0 ? 'OVERDUE' : fmtD(x.expires_at)}</span>
              </div>))}
          </div>
        </div>}
    </div>
  );
}
